import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getNews, getTeams, getNextGame } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Users, Calendar, ArrowRight, ChevronRight, MapPin, Clock, Crosshair } from 'lucide-react';

const HERO_BG = 'https://customer-assets-eiarnc6j.emergentagent.net/job_dart-league-pro/artifacts/nfkbwswo_26BFAE61-5C52-47F7-B88C-96F1B4208277.png';

const HomePage = () => {
  const [news, setNews] = useState([]);
  const [teams, setTeams] = useState([]);
  const [nextGames, setNextGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsRes, teamsRes, nextGameRes] = await Promise.all([
          getNews(),
          getTeams(),
          getNextGame(),
        ]);
        setNews(newsRes.data.slice(0, 3));
        setTeams(teamsRes.data);
        setNextGames(Array.isArray(nextGameRes.data) ? nextGameRes.data : []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center justify-center"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-heading font-black uppercase tracking-tighter mb-6 animate-fade-in" style={{ textShadow: '0 0 10px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.7), 2px 2px 4px rgba(0,0,0,1), -2px -2px 4px rgba(0,0,0,1)' }}>
            <span className="text-white">House of </span>
            <span className="text-primary">Darts</span>
          </h1>
          <p className="text-lg md:text-xl text-white mb-8 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s', textShadow: '0 0 8px rgba(0,0,0,0.9), 1px 1px 3px rgba(0,0,0,1)' }}>
            Dein Dartverein für Spaß, Sport und Gemeinschaft. 
            Werde Teil unserer Dart-Familie und erlebe unvergessliche Momente!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <Link to="/mitglied-werden">
              <Button className="btn-primary text-lg px-8 py-4" data-testid="hero-cta-member">
                Mitglied werden
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/kontakt">
              <Button className="btn-outline text-lg px-8 py-4" data-testid="hero-cta-about">
                Mehr erfahren
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Nächste Spiele Section */}
      {nextGames.length > 0 && (
        <section className="section-padding bg-surface" data-testid="next-game-section">
          <div className="container-max">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl md:text-4xl font-heading font-bold uppercase tracking-tighter">
                Nächste <span className="text-primary">Spiele</span>
              </h2>
              <Link to="/spielplan" className="text-primary hover:text-primary-hover flex items-center gap-1">
                Spielplan <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Date header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-primary/10 rounded-sm px-4 py-2 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                <span className="font-heading font-bold text-primary">
                  {new Date(nextGames[0].datum + 'T00:00:00').toLocaleDateString('de-AT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                </span>
              </div>
              <span className="text-text-secondary text-sm">{nextGames.length} {nextGames.length === 1 ? 'Spiel' : 'Spiele'}</span>
            </div>

            <div className="space-y-3">
              {nextGames.map((game) => (
                <Card key={game.game_id} className="card-elevated border-primary/20 overflow-hidden" data-testid={`next-game-card-${game.game_id}`}>
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-accent text-xs font-bold uppercase tracking-wider mb-1">{game.team_name}</p>
                        <h3 className="text-lg sm:text-xl font-heading font-bold uppercase">
                          {game.heim 
                            ? <>{game.team_name} <span className="text-text-secondary font-normal text-base">vs</span> {game.gegner}</>
                            : <>{game.gegner} <span className="text-text-secondary font-normal text-base">vs</span> {game.team_name}</>
                          }
                        </h3>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-text-secondary">
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4 text-primary" />
                            {game.zeit} Uhr
                          </span>
                          <span className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4 text-primary" />
                            {game.ort || 'House of Darts'}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Crosshair className="w-4 h-4 text-primary" />
                            {game.heim ? 'Heimspiel' : 'Auswärtsspiel'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-6 text-center">
              <Link to="/spielplan">
                <Button className="btn-primary" data-testid="next-game-spielplan-btn">
                  Zum Spielplan
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Quick Access Bento Grid */}
      <section className="section-padding bg-background" data-testid="quick-access-section">
        <div className="container-max">
          <h2 className="text-3xl md:text-4xl font-heading font-bold uppercase tracking-tighter text-center mb-12">
            Schnellzugriff
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/mitglied-werden" data-testid="quick-membership">
              <Card className="card-surface hover:border-primary/50 transition-all duration-300 group h-full">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <Users className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-bold uppercase mb-2">Mitglied werden</h3>
                  <p className="text-text-secondary text-sm">Werde Teil unserer Community</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/mannschaften" data-testid="quick-teams">
              <Card className="card-surface hover:border-primary/50 transition-all duration-300 group h-full">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                    <Target className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-xl font-heading font-bold uppercase mb-2">Teams</h3>
                  <p className="text-text-secondary text-sm">Unsere Mannschaften</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/spielplan" data-testid="quick-spielplan">
              <Card className="card-surface hover:border-primary/50 transition-all duration-300 group h-full">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <Crosshair className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-bold uppercase mb-2">Spielplan</h3>
                  <p className="text-text-secondary text-sm">Alle Spiele im Überblick</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/termine" data-testid="quick-termine">
              <Card className="card-surface hover:border-primary/50 transition-all duration-300 group h-full">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                    <Calendar className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-xl font-heading font-bold uppercase mb-2">Termine</h3>
                  <p className="text-text-secondary text-sm">Turniere, Training & mehr</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="section-padding bg-surface" data-testid="news-section">
        <div className="container-max">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold uppercase tracking-tighter">
              Aktuelles
            </h2>
            <Link to="/news" className="text-primary hover:text-primary-hover flex items-center gap-1">
              Alle News <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="card-elevated animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-4 bg-white/10 rounded w-1/4 mb-4" />
                    <div className="h-6 bg-white/10 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-white/10 rounded w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : news.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.map((item, index) => (
                <Link key={item.news_id} to={`/news/${item.news_id}`}>
                  <Card 
                    className="card-elevated hover:border-primary/30 transition-all duration-300 cursor-pointer h-full"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CardContent className="p-6">
                      <span className="text-xs text-accent uppercase tracking-wider">
                        {formatDate(item.created_at)}
                      </span>
                      <h3 className="text-xl font-heading font-bold uppercase mt-2 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-text-secondary text-sm line-clamp-3">
                        {item.excerpt || item.content}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-text-secondary text-center py-12">Keine News vorhanden.</p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary" data-testid="cta-section">
        <div className="container-max text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-black uppercase tracking-tighter text-white mb-4">
            Bereit für den Wurf?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Werde jetzt Mitglied bei House of Darts und erlebe Dart auf einem neuen Level. 
            Training, Turniere und eine großartige Community warten auf dich!
          </p>
          <Link to="/mitglied-werden">
            <Button className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-4 font-bold" data-testid="cta-join-btn">
              Jetzt Mitglied werden
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
